import { CATEGORY_COLORS } from "../../../constants/categoryColors";

export const RANGE_PRESET = {
  WEEK: "week",
  MONTH: "month",
  CUSTOM: "custom",
};

const CATEGORY_META = {
  incident: { label: "IMPORTANT MOMENT", order: 1 },
  importantMoment: { label: "IMPORTANT MOMENT", order: 1 },
  behavior: { label: "BEHAVIOR", order: 2 },
  health: { label: "HEALTH", order: 3 },
  mood: { label: "MOOD", order: 4 },
  sleep: { label: "SLEEP", order: 5 },
  food: { label: "FOOD", order: 6 },
  medication: { label: "MEDICATION", order: 7 },
  milestone: { label: "MILESTONE", order: 7 },
  notes: { label: "NOTE", order: 8 },
  log: { label: "DAILY LOG", order: 8 },
  daily_note: { label: "DAILY LOG", order: 8 },
};

export const THERAPY_PREP_FILTERS = [
  { key: "sleep", label: "Sleep" },
  { key: "behavior", label: "Behavior" },
  { key: "medication", label: "Medication" },
  { key: "food", label: "Food" },
  { key: "notes", label: "Notes" },
];

export const toEntryDate = (timestamp) => timestamp?.toDate?.() || new Date(timestamp);

export const startOfDay = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

export const endOfDay = (date) => {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
};

export const getWeekRange = () => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(end.getDate() - 6);
  return { start, end };
};

export const getMonthRange = () => {
  const end = new Date();
  const start = new Date(end.getFullYear(), end.getMonth(), 1);
  return { start, end };
};

export const isValidDate = (date) => date instanceof Date && !Number.isNaN(date.getTime());

export const formatCompactDate = (date) =>
  date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

export const formatLongDate = (date) =>
  date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

export const formatPeriodLabel = (start, end, preset) => {
  if (!isValidDate(start) || !isValidDate(end)) return "";

  if (preset === RANGE_PRESET.WEEK) {
    if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
      return `Week of ${start.toLocaleDateString("en-US", { month: "long", day: "numeric" })} - ${end.getDate()}, ${end.getFullYear()}`;
    }

    if (start.getFullYear() === end.getFullYear()) {
      return `Week of ${start.toLocaleDateString("en-US", { month: "long", day: "numeric" })} - ${end.toLocaleDateString("en-US", { month: "long", day: "numeric" })}, ${end.getFullYear()}`;
    }

    return `Week of ${formatLongDate(start)} - ${formatLongDate(end)}`;
  }

  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${start.toLocaleDateString("en-US", { month: "long" })} ${start.getDate()} – ${end.getDate()}, ${end.getFullYear()}`;
  }

  if (start.getFullYear() === end.getFullYear()) {
    return `${start.toLocaleDateString("en-US", { month: "long", day: "numeric" })} – ${end.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`;
  }

  return `${formatLongDate(start)} – ${formatLongDate(end)}`;
};

export const getRangeHelperText = (preset) => {
  if (preset === RANGE_PRESET.WEEK) {
    return "This Week — best for a focused snapshot before an upcoming session.";
  }
  if (preset === RANGE_PRESET.MONTH) {
    return "This Month — a wider view of progress, routines, and repeated challenges.";
  }
  return "Custom Range — choose the exact window you want to review.";
};

export const sanitizeSummary = (value = "") =>
  value.replace(/^(Behavior|Health|Mood|Sleep|Food|Milestone|Daily Log|Important Moment):\s*/i, "").trim();

export const getMeaningfulSummary = (entry) => {
  const content = sanitizeSummary(
    entry.content || entry.originalData?.text || entry.originalData?.notes || entry.originalData?.description || ""
  );
  if (content) return content;

  const title = sanitizeSummary(entry.title || "");
  if (title && title !== "Entry" && title !== "Daily Care") return title;

  return "";
};

export const getLoggedByName = (entry) => {
  const value =
    entry.originalData?.authorName ||
    entry.originalData?.loggedBy?.name ||
    entry.originalData?.author ||
    entry.originalData?.createdByName ||
    entry.author;

  if (!value || value === "Unknown") {
    return "";
  }

  return String(value).trim();
};

export const getInitials = (name = "") => {
  const parts = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
};

export const isImportantMomentEntry = (entry) =>
  entry.type === "incident" || entry.isImportantMoment || entry.importantMoment || entry.originalData?.importantMoment;

export const getCategoryMeta = (entry) => {
  if (isImportantMomentEntry(entry)) {
    return CATEGORY_META.importantMoment;
  }

  return CATEGORY_META[entry.type] || CATEGORY_META.log;
};

export const getCategoryColors = (entry) => {
  if (isImportantMomentEntry(entry)) {
    return CATEGORY_COLORS.importantMoment;
  }

  return CATEGORY_COLORS[entry.type] || CATEGORY_COLORS.log;
};

export const normalizeText = (value = "") => String(value ?? "").replace(/\s+/g, " ").trim();

export const getEntryDayKey = (entry) => startOfDay(toEntryDate(entry.timestamp)).toISOString();

export const getEntrySearchText = (entry = {}) =>
  normalizeText([
    getMeaningfulSummary(entry),
    entry.title,
    entry.content,
    entry.notes,
    entry.description,
    entry.originalData?.text,
    entry.originalData?.notes,
    entry.originalData?.description,
    entry.incidentData?.notes,
    entry.incidentData?.description,
    entry.categoryLabel,
    entry.titlePrefix,
    entry.medicationName,
    entry.medicationDetails?.medicationName,
    entry.medicationDetails?.name,
    entry.medicationScheduleDose,
    entry.medicationDetails?.dosage,
    entry.medicationDetails?.dose,
    entry.medicationScheduleUnit,
    entry.medicationDetails?.unit,
  ].filter(Boolean).join(" "));

export const isSleepEntry = (entry = {}) => {
  const type = String(entry.type || entry.timelineType || entry.category || entry.logCategory || "").toLowerCase();
  return type.includes("sleep") || /\bsleep\b/i.test(getEntrySearchText(entry));
};

export const isFoodEntry = (entry = {}) => {
  const type = String(entry.type || entry.timelineType || entry.category || entry.logCategory || "").toLowerCase();
  return type.includes("food") || /\bfood\b/i.test(getEntrySearchText(entry));
};

export const isMedicationEntry = (entry = {}) => {
  const type = String(entry.type || entry.timelineType || entry.category || entry.logCategory || "").toLowerCase();
  if (type.includes("medication") || type === "meds") {
    return true;
  }

  const label = normalizeText([entry.titlePrefix, entry.categoryLabel, entry.title].filter(Boolean).join(" ")).toLowerCase();
  return /\bmedication(s)?\b/.test(label) || /\bmeds?\b/.test(label);
};

export const isBehaviorEntry = (entry = {}) => {
  if (isImportantMomentEntry(entry)) {
    return true;
  }

  const type = String(entry.type || entry.timelineType || entry.category || entry.logCategory || "").toLowerCase();
  if (type.includes("behavior") || type === "incident") {
    return true;
  }

  return /behavior|meltdown|tantrum|aggress|hit|bite|kick|elop|refus|unsafe|restraint/i.test(getEntrySearchText(entry));
};

export const getTherapyPrepCategory = (entry = {}) => {
  if (isSleepEntry(entry)) return "sleep";
  if (isBehaviorEntry(entry)) return "behavior";
  if (isMedicationEntry(entry)) return "medication";
  if (isFoodEntry(entry)) return "food";
  return "notes";
};

export const groupMedicationEntriesByDose = (entries = []) => {
  const grouped = entries.reduce((acc, entry) => {
    const name = normalizeText(
      entry.medicationName
      || entry.medicationDetails?.medicationName
      || entry.medicationDetails?.name
      || entry.title
      || entry.categoryLabel
      || entry.titlePrefix
      || "Medication"
    ) || "Medication";
    const dose = normalizeText([
      entry.medicationScheduleDose || entry.medicationDetails?.dosage || entry.medicationDetails?.dose || entry.dosage || entry.dose,
      entry.medicationScheduleUnit || entry.medicationDetails?.unit || entry.unit,
    ].filter(Boolean).join(" "));
    const status = String(entry.status || entry.backfillStatus || "").toLowerCase() === "missed" ? "missed" : "given";
    const key = `${name.toLowerCase()}|${dose.toLowerCase()}`;

    if (!acc[key]) {
      acc[key] = {
        name,
        dose,
        givenCount: 0,
        missedCount: 0,
      };
    }

    acc[key][status === "missed" ? "missedCount" : "givenCount"] += 1;
    return acc;
  }, {});

  return Object.values(grouped).sort((a, b) => (b.givenCount + b.missedCount) - (a.givenCount + a.missedCount) || a.name.localeCompare(b.name));
};

export const buildDateSections = (entries) => {
  const grouped = entries.reduce((acc, entry) => {
    const date = toEntryDate(entry.timestamp);
    const key = startOfDay(date).toISOString();
    if (!acc[key]) {
      acc[key] = { date, items: [] };
    }
    acc[key].items.push(entry);
    return acc;
  }, {});

  return Object.values(grouped)
    .sort((a, b) => b.date - a.date)
    .map((section) => ({
      ...section,
      items: section.items.sort((a, b) => toEntryDate(b.timestamp) - toEntryDate(a.timestamp)),
    }));
};
